/**
 * Honest match timing for a feed that has NO live-scores endpoint.
 *
 * We only know the scheduled kick-off time, so:
 *  - before kick-off        → 'upcoming'  (+ live countdown)
 *  - up to MATCH_WINDOW after → 'inplay'  (kick-off passed; likely underway)
 *  - beyond the window        → 'ended'   (so matches don't show "LIVE" forever)
 *
 * We never claim a live score we don't have.
 */
import { useEffect, useState } from 'react';

// Generous window so long formats (cricket, tennis) still read as in-play.
const MATCH_WINDOW_MS = 6 * 60 * 60 * 1000;

export type MatchPhase = 'upcoming' | 'inplay' | 'ended';

export function matchPhase(startsAt: string): MatchPhase {
  const t = Date.parse(startsAt);
  if (Number.isNaN(t)) return 'upcoming';
  const dt = Date.now() - t;
  if (dt < 0) return 'upcoming';
  if (dt < MATCH_WINDOW_MS) return 'inplay';
  return 'ended';
}

/** "3d 4h" / "2h 15m" / "8m 30s" until kick-off; '' if already started. */
export function countdownTo(startsAt: string): string {
  const ms = Date.parse(startsAt) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return '';
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** "11 min ago" / "1h 22m ago" since kick-off; '' if not started. */
export function sinceKickoff(startsAt: string): string {
  const ms = Date.now() - Date.parse(startsAt);
  if (Number.isNaN(ms) || ms < 0) return '';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m ago` : `${Math.max(m, 0)} min ago`;
}

/** Re-render hook: returns a value that changes every `intervalMs` so
 *  countdowns tick. Defaults to 1s. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
